import { useEffect, useRef, useState } from 'react'
import type { GridApi } from 'ag-grid-community'
import {
  hasScrollableRight,
  pinnedRightWidth,
  scrollFadeInset,
} from './overflow'

/**
 * AG Grid 36's horizontal scrollbar. VERIFIED against the installed
 * ag-grid-community@36.0.1 bundle, not guessed — `ag-center-cols-viewport` (the
 * name every pre-v33 example uses) does not exist in this version at all.
 *
 * The viewport is the scrollable element; the container inside it is sized to
 * the TOTAL column width, which is what makes it the right thing to observe for
 * "the sheet got wider without anyone scrolling or resizing the page".
 */
const SCROLL_VIEWPORT = '.ag-body-horizontal-scroll-viewport'
const SCROLL_CONTAINER = '.ag-body-horizontal-scroll-container'

type Fade = { visible: boolean; inset: number }

const HIDDEN: Fade = { visible: false, inset: 0 }

/**
 * SN-8356 — the lifecycle behind the edge fade.
 *
 * `domLayout='autoHeight'` puts AG Grid's scrollbar below EVERY row, so on a
 * 20-row sheet the only existing cue that the sheet continues sideways is off
 * screen. This measures whether it does, and how far in from the right edge the
 * cue has to stop.
 *
 * Fails closed throughout: anything it cannot resolve leaves the affordance off
 * rather than drawing a fade over a sheet that fits.
 */
export const useHorizontalScrollAffordance = (enabled: boolean) => {
  'use memo'

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const apiRef = useRef<GridApi | null>(null)
  const remeasureRef = useRef<() => void>(() => {})
  const [fade, setFade] = useState<Fade>(HIDDEN)

  /**
   * Captured by COMPOSING the caller's `onGridReady`, never replacing it — the
   * wrapper must not quietly eat a grid option a feature passed. The api is the
   * only public way to ask how wide the pinned-right section is
   * (`getDisplayedRightColumns` × `getActualWidth`, both public in 36.0.1).
   */
  const captureApi = (api: GridApi) => {
    apiRef.current = api
    // `gridReady` precedes final layout, so this is a request to re-measure on
    // the next frame, not a measurement.
    remeasureRef.current()
  }

  useEffect(() => {
    if (!enabled) {
      setFade((prev) => (prev === HIDDEN ? prev : HIDDEN))

      return
    }

    const wrapper = wrapperRef.current
    if (
      !wrapper ||
      typeof window === 'undefined' ||
      typeof window.ResizeObserver !== 'function' ||
      typeof window.requestAnimationFrame !== 'function'
    ) {
      return
    }

    let frame: number | null = null
    let observedContainer: Element | null = null

    /*
     * One observer per thing that can change the answer:
     *   - the container's width IS the total column width, so this catches a
     *     column resize and the MANAGE column's 76↔152 flip — both of which move
     *     `scrollWidth` with no scroll and no wrapper resize;
     *   - the wrapper's width is the viewport width.
     *
     * Constructed FIRST, and dispatching through an arrow, because `measure`
     * has to re-observe the container and `schedule` has to reach `measure`.
     */
    const containerObserver = new window.ResizeObserver(() => schedule())
    const wrapperObserver = new window.ResizeObserver(() => schedule())

    const measure = () => {
      frame = null

      const viewport = wrapper.querySelector<HTMLElement>(SCROLL_VIEWPORT)
      if (!viewport) {
        setFade((prev) => (prev === HIDDEN ? prev : HIDDEN))

        return
      }

      /*
       * The container is recreated whenever the grid remounts (a modal that
       * hides and shows, for instance), so it is re-resolved on every measure
       * and re-observed only when the NODE changed — leaving a stale one
       * observed would silently stop reporting width changes.
       */
      const container = viewport.querySelector<HTMLElement>(SCROLL_CONTAINER)
      if (container !== observedContainer) {
        if (observedContainer) {
          containerObserver.unobserve(observedContainer)
        }
        observedContainer = container
        if (container) {
          containerObserver.observe(container)
        }
      }

      const visible = hasScrollableRight({
        scrollLeft: viewport.scrollLeft,
        scrollWidth: viewport.scrollWidth,
        clientWidth: viewport.clientWidth,
      })

      const api = apiRef.current
      const pinned =
        api && !api.isDestroyed()
          ? pinnedRightWidth(
              api
                .getDisplayedRightColumns()
                .map((column) => column.getActualWidth())
            )
          : 0

      const inset = scrollFadeInset({
        wrapperRight: wrapper.getBoundingClientRect().right,
        viewportRight: viewport.getBoundingClientRect().right,
        pinnedWidth: pinned,
      })

      setFade((prev) =>
        prev.visible === visible && prev.inset === inset
          ? prev
          : { visible, inset }
      )
    }

    const schedule = () => {
      if (frame !== null) {
        return
      }
      frame = window.requestAnimationFrame(measure)
    }

    /*
     * Capture phase on the WRAPPER, which is the one node that survives the
     * grid re-creating its internals. `scroll` does not bubble, but a
     * non-bubbling event still runs capturing listeners on its ancestors — so
     * this hears both the real viewport and the scrollbar without holding a
     * reference to either, and without intercepting a caller's `onBodyScroll`.
     */
    wrapper.addEventListener('scroll', schedule, {
      capture: true,
      passive: true,
    })
    wrapperObserver.observe(wrapper)
    remeasureRef.current = schedule
    schedule()

    return () => {
      wrapper.removeEventListener('scroll', schedule, { capture: true })
      wrapperObserver.disconnect()
      containerObserver.disconnect()
      observedContainer = null
      remeasureRef.current = () => {}
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [enabled])

  return { wrapperRef, captureApi, fade }
}
